import { Box, Button, Group, List, Text } from '@mantine/core';
import { CaretRightIcon } from '@phosphor-icons/react';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useHoverKeyboardNavigation } from '../hooks/useHoverKeyboardNavigation';
import { type EntityTypeMeta } from '../types';

export type EntitySelectorRef = {
    onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

export interface EntitySelectorProps {
    availableEntityTypes: string[];
    onEntityTypeSelect: (entityType: string) => void;
    query: string;
    entityMeta: EntityTypeMeta;
}

export const EntitySelector = forwardRef<
    EntitySelectorRef,
    EntitySelectorProps
>(({ availableEntityTypes, onEntityTypeSelect, query, entityMeta }, ref) => {
    const filteredEntityTypes = availableEntityTypes.filter((entityType) => {
        const meta = entityMeta[entityType];
        if (!meta) return false;
        const lowercaseQuery = query.toLowerCase();
        return (
            meta.label.toLowerCase().includes(lowercaseQuery) ||
            entityType.toLowerCase().includes(lowercaseQuery)
        );
    });

    const {
        activeIndex,
        upHandler,
        downHandler,
        enterHandler,
        handleMouseEnter,
        handleMouseLeave,
        reset,
    } = useHoverKeyboardNavigation(filteredEntityTypes.length);

    const selectItem = (index: number) => {
        const entityType = filteredEntityTypes[index];
        onEntityTypeSelect(entityType);
    };

    useEffect(() => {
        reset();
    }, [filteredEntityTypes.length, reset]);

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }) => {
            if (event.key === 'ArrowUp') {
                upHandler();
                return true;
            }

            if (event.key === 'ArrowDown') {
                downHandler();
                return true;
            }

            if (event.key === 'Enter') {
                const index = enterHandler();
                selectItem(index);
                return true;
            }

            return false;
        },
    }));

    if (!filteredEntityTypes.length) {
        return (
            <Box
                className="p-3"
                maw={300}
                w={200}
                // Prevent the editor from losing focus when clicking inside the popup
                onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => {
                    event.preventDefault();
                }}
            >
                <Text className="text-xs text-white/40">Nothing found.</Text>
            </Box>
        );
    }

    return (
        <Box
            p={4}
            maw={300}
            w={200}
            onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => {
                event.preventDefault();
            }}
        >
            <List
                withPadding={false}
                listStyleType="none"
                mah={300}
                styles={() => ({
                    root: {
                        overflowY: 'auto',
                        paddingInlineStart: 0,
                    },
                    itemWrapper: {
                        width: '100%',
                    },
                    itemLabel: {
                        width: '100%',
                    },
                })}
            >
                {filteredEntityTypes.map((entityType, index) => {
                    const meta = entityMeta[entityType];
                    if (!meta) return null;
                    const IconComponent = meta.icon;
                    const isActive = index === activeIndex;

                    return (
                        <List.Item key={entityType}>
                            <Button
                                onClick={() => selectItem(index)}
                                onMouseEnter={() => handleMouseEnter(index)}
                                onMouseLeave={handleMouseLeave}
                                onMouseDown={(
                                    event: React.MouseEvent<HTMLButtonElement>,
                                ) => {
                                    event.preventDefault();
                                }}
                                style={(theme) => ({
                                    padding: 0,
                                    width: '100%',
                                    fontSize: theme.fontSizes.sm,
                                    fontWeight: 500,
                                    backgroundColor: isActive
                                        ? 'rgb(var(--color-gray-700)) !important'
                                        : 'transparent',

                                    color: 'rgb(var(--color-white))',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor:
                                            'rgb(var(--color-gray-600)) !important',
                                    },
                                    '& .mantine-Button-inner': {
                                        justifyContent: 'flex-start',
                                        paddingLeft: 8,
                                        paddingRight: 4,
                                        paddingTop: 8,
                                        paddingBottom: 8,
                                    },
                                    '& .mantine-Button-label': {
                                        justifyContent: 'flex-start',
                                        padding: '0px !important',
                                    },
                                })}
                            >
                                <Box className="flex w-full items-center justify-between gap-2">
                                    <Group className="gap-1">
                                        {IconComponent && (
                                            <IconComponent
                                                size={14}
                                                color="rgb(var(--color-gray-100))"
                                            />
                                        )}
                                        <Box>
                                            <Text className="text-sm font-medium text-gray-100">
                                                {meta.label}
                                            </Text>
                                        </Box>
                                    </Group>
                                    <CaretRightIcon
                                        size={14}
                                        weight="bold"
                                        color="rgb(var(--color-gray-100))"
                                    />
                                </Box>
                            </Button>
                        </List.Item>
                    );
                })}
            </List>
        </Box>
    );
});
